import { Badge } from "@/components/ui/badge";
import { Building2, User, FileText, Calculator, Package, Link2 } from "lucide-react";

const Field = ({ label, value, testid }) => (
  <div className="flex flex-col">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-800 break-words" data-testid={testid}>
      {value === undefined || value === null || value === "" ? "—" : value}
    </span>
  </div>
);

const SectionCard = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
    <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
      <Icon className="w-4 h-4 text-slate-600" />
      <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const paymentMethodLabel = (m) => {
  const map = { 1: "Efectivo", 2: "Cheque/Transferencia/Depósito", 3: "Tarjeta Crédito/Débito", 4: "Venta a Crédito", 5: "Bonos o Certificados de Regalo", 6: "Permuta", 7: "Otras Formas de Venta" };
  return map[m] || `Método ${m}`;
};

const modificationCodeLabel = (c) => {
  const map = { 1: "Anula NCF anterior", 2: "Corrige texto del comprobante fiscal", 3: "Corrige montos del NCF modificado", 4: "Reemplazo comprobante emitido en contingencia", 5: "Referencia factura de consumo electrónica" };
  return map[c] !== undefined ? `${c} - ${map[c]}` : (c ?? "—");
};

const incomeTypeLabel = (c) => {
  const map = {
    "01": "Ingresos por operaciones (no financieros)",
    "02": "Ingresos financieros",
    "03": "Ingresos extraordinarios",
    "04": "Ingresos por arrendamientos",
    "05": "Ingresos por venta de activo depreciable",
    "06": "Otros Ingresos"
  };

  const code = String(c).padStart(2, "0");
  return map[code] !== undefined ? `${code} - ${map[code]}` : (c ?? "—");
};
const paymentTypeLabel = (c) => {
  const map = {
    1: "Contado",
    2: "Crédito",
    3: "Gratuito"
  };

  return map[c] !== undefined ? `${c} - ${map[c]}` : (c ?? "—");
};

const billingIndicatorLabel = (c) => {
  const map = {
    0: "No Facturable",
    1: "ITBIS 1 (18%)",
    2: "ITBIS 2 (16%)",
    3: "ITBIS 3 (0%)",
    4: "Exento (E)"
  };

  return map[c] !== undefined ? `${c} - ${map[c]}` : (c ?? "—");
};

export const FacturaPayloadView = ({ payload, formatCurrency }) => {
  if (!payload || typeof payload !== "object" || Object.keys(payload).length === 0) {
    return (
      <div className="text-center py-10 text-slate-500 text-sm" data-testid="factura-empty">
        No hay datos de factura disponibles para este comprobante.
      </div>
    );
  }

  const buyer = payload.buyer || {};
  const sender = payload.sender || {};
  const idDoc = payload.idDoc || {};
  const totals = payload.totals || {};
  const items = Array.isArray(payload.itemDetails) ? payload.itemDetails : [];
  const paymentForms = Array.isArray(idDoc.paymentFormsTable) ? idDoc.paymentFormsTable : [];
  const infoRef = payload.informationReference || null;

  const money = (v) => (formatCurrency ? formatCurrency(Number(v || 0)) : Number(v || 0).toFixed(2));

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="factura-view">
      {/* e-NCF Header */}
      <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="factura-encf-header">
        <div>
          <p className="text-xs text-emerald-700">e-NCF</p>
          <p className="text-lg font-bold font-mono text-emerald-900" data-testid="factura-encf">{idDoc.encf || "—"}</p>
        </div>
        <Badge className="bg-emerald-200 text-emerald-900 border-emerald-200 text-lg">
          {money(totals.totalAmount)}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Emisor */}
        <SectionCard icon={Building2} title="Emisor" testid="factura-sender">
          <div className="grid grid-cols-1 gap-3">
            <Field label="RNC" value={sender.rnc} testid="sender-rnc" />
            <Field label="Razón Social" value={sender.companyName} testid="sender-name" />
            <Field label="Nombre Comercial" value={sender.tradename} />
            <Field label="Correo" value={sender.mail} />
            <Field label="Teléfono" value={Array.isArray(sender.phoneNumber) ? sender.phoneNumber.join(", ") : sender.phoneNumber} />
            <Field label="Actividad Económica" value={sender.economicActivity} />
            <Field label="Sitio Web" value={sender.webSite} />
            <Field label="Dirección" value={sender.address} />
          </div>
        </SectionCard>

        {/* Comprador */}
        <SectionCard icon={User} title="Comprador" testid="factura-buyer">
          <div className="grid grid-cols-1 gap-3">
            <Field label="RNC" value={buyer.rnc} testid="buyer-rnc" />
            <Field label="Razón Social" value={buyer.companyName} testid="buyer-name" />
            <Field label="Nombre Comercial" value={buyer.tradename} />
            <Field label="Correo" value={buyer.mail} />
            <Field label="Contacto" value={buyer.contact} />
            <Field label="Dirección" value={buyer.address} />
          </div>
        </SectionCard>
      </div>

      {/* Documento */}
      <SectionCard icon={FileText} title="Datos del Documento" testid="factura-doc">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="e-NCF" value={idDoc.encf} />
          <Field label="Tipo de Ingreso" value={incomeTypeLabel(idDoc.incomeType)} />
          <Field label="Tipo de Pago" value={paymentTypeLabel(idDoc.paymentType)} />
          <Field label="Plazo de Pago" value={idDoc.paymentTerm} />
          <Field label="Fecha Límite de Pago" value={idDoc.paymentDeadline} />
          <Field label="Fecha Venc. Secuencia" value={idDoc.sequenceDueDate} />
          <Field label="Indicador Monto Gravado" value={idDoc.taxAmountIndicator} />
          <Field label="Indicador Nota de Crédito" value={idDoc.creditNoteIndicator} />
        </div>
        {paymentForms.length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-slate-500 mb-2">Formas de Pago</p>
            <div className="space-y-1.5">
              {paymentForms.map((pf, i) => (
                <div key={i} className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-2" data-testid={`payment-form-${i}`}>
                  <span className="text-slate-700">{paymentMethodLabel(pf.paymentMethod)}</span>
                  <span className="font-medium text-slate-900">{money(pf.paymentAmount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      {/* Detalle de Ítems */}
      <SectionCard icon={Package} title="Detalle de Ítems" testid="factura-items">
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">Sin ítems.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-2">Descripción</th>
                  <th className="py-2 pr-2 text-right">Cant.</th>
                  <th className="py-2 pr-2 text-right">P. Unitario</th>
                  <th className="py-2 text-right">Monto</th>
                  <th className="py-2 pr-2 text-right">Impuesto</th>

                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`factura-item-${i}`}>
                    <td className="py-2 pr-2 text-slate-500">{it.lineNumber ?? i + 1}</td>
                    <td className="py-2 pr-2 text-slate-800">{it.itemName || "—"}</td>
                    <td className="py-2 pr-2 text-right">{it.quantityItem ?? 1}</td>
                    <td className="py-2 pr-2 text-right">{money(it.unitPriceItem)}</td>
                    <td className="py-2 text-right font-medium">{money(it.itemAmount)}</td>
                    <td className="py-2 pr-2 text-right">{billingIndicatorLabel(it.billingIndicator)}</td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Información de Referencia (Notas de Crédito/Débito) */}
      {infoRef && (
        <SectionCard icon={Link2} title="Información de Referencia" testid="factura-info-reference">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="NCF Modificado" value={infoRef.ncfModified} testid="inforef-ncf-modified" />
            <Field label="Fecha NCF Modificado" value={infoRef.ncfModifiedDate} testid="inforef-ncf-date" />
            <Field label="Código de Modificación" value={modificationCodeLabel(infoRef.modificationCode)} testid="inforef-mod-code" />
          </div>
        </SectionCard>
      )}

      {/* Totales */}
      <SectionCard icon={Calculator} title="Totales" testid="factura-totals">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Monto Gravado" value={money(totals.totalTaxedAmount ?? totals.i1AmountTaxed)} testid="totals-taxed" />
          <Field label="Monto Exento" value={money(totals.exemptAmount)} />
          <Field label={`ITBIS (${totals.itbisS1 ?? 18}%)`} value={money(totals.itbis1Total ?? totals.itbisTotal)} testid="totals-itbis" />
          {Number(totals.itbis2Total || 0) > 0 && (
            <Field label={`ITBIS (${totals.itbisS2 ?? 16}%)`} value={money(totals.itbis2Total)} testid="totals-itbis2" />
          )}
        </div>
        <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-900 px-4 py-3">
          <span className="text-sm text-slate-300">Total</span>
          <span className="text-lg font-bold text-white" data-testid="totals-total">{money(totals.totalAmount)}</span>
        </div>
      </SectionCard>
    </div>
  );
};

export default FacturaPayloadView;
